import axios from 'axios';
import { AxiosService } from '../../axios/axios.service';
import { tAxiosCustomConfig } from '../../axios/axios.types';
import { tLead } from '../../providers/SocketsProvider/Sockets.types';
import {
  tAgendaEvent,
  tAgendaResponse,
  tCreateEventPayload,
  tCreateTemplatePayload,
  tCreateTemplateResponse,
  tEventTypeRequest,
  tTemplateV2,
  tUpdateEventPayload,
  tUpdateTemplatePayload,
} from './Agenda.types';
import { TemplateIsInUse } from './agenda.errors';
import {
  tCreateEventRequest,
  tEventResponse,
} from './components/Calendar/Calendar.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';

export const AgendaService = {
  getCurrentTemplates: async function (
    currentWorkspaceId: string,
  ): Promise<tAgendaResponse<{ templates: tTemplateV2[] }>> {
    try {
      const response = await AxiosService.get<
        tAgendaResponse<{ templates: tTemplateV2[] }>
      >(`agenda/templates?workspaceId=${currentWorkspaceId}`);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  getLeadInformation: async function (
    wsId: string,
    leadId: string,
    userId: string,
  ): Promise<tAgendaResponse<tLead>> {
    try {
      const config: tAxiosCustomConfig = {
        headers: {
          'Auth-id': userId,
        },
        isLegacy: true,
      };

      const response = await AxiosService.get<tAgendaResponse<tLead>>(
        `ws/${wsId}/lds/${leadId}`,
        config,
      );

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  getEventTypes: function (): tEventTypeRequest[] {
    return [
      {
        id: '0da16f64-990d-4934-b51c-8dd7658cd5a1',
        name: 'Call',
      },
      {
        id: 'ab93570c-157c-4e10-8a34-2226ffac51ff',
        name: 'Meeting',
      },
      {
        id: '15de4b0c-9677-4fe0-8a32-a9ffdc37251e',
        name: 'Task',
      },
    ];
  },

  createAgendaTemplate: async function (
    template: tCreateTemplatePayload,
  ): Promise<tAgendaResponse<tCreateTemplateResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      const response = await AxiosService.post<
        tCreateTemplatePayload,
        tAgendaResponse<tCreateTemplateResponse>
      >(`calendar/templates`, template, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  createAgendaEvent: async function (
    event: tCreateEventPayload,
  ): Promise<tAgendaResponse<tAgendaEvent>> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      const response = await AxiosService.post<
        tCreateEventPayload,
        tAgendaResponse<tAgendaEvent>
      >(`calendar/events`, event, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  createAgendaLeadEvent: async function (
    event: Partial<tCreateEventRequest>,
    leadID: string,
  ): Promise<tAgendaResponse<tAgendaEvent>> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      const response = await AxiosService.post<
        Partial<tCreateEventRequest>,
        tAgendaResponse<tAgendaEvent>
      >(`calendar/events/lead/${leadID}`, event, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  updateAgendaTemplate: async function (
    updatedTemplate: tUpdateTemplatePayload,
    templateID: string,
  ): Promise<tAgendaResponse<tCreateTemplateResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      const response = await AxiosService.patchById<
        tUpdateTemplatePayload,
        tAgendaResponse<tCreateTemplateResponse>
      >(`calendar/templates`, templateID, updatedTemplate, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  updateAgendaEvent: async function (
    event: tUpdateEventPayload,
    eventID: string,
  ): Promise<tAgendaResponse<tAgendaEvent>> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      const response = await AxiosService.patchById<
        tUpdateEventPayload,
        tAgendaResponse<tAgendaEvent>
      >(`calendar/events`, eventID, event, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  deleteCalendarTemplate: async function (templateID: string): Promise<void> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      await AxiosService.delete(`calendar/templates/${templateID}`, config);
    } catch (err) {
      if (axios.isAxiosError(err) && err?.response?.status === 412) {
        return Promise.reject(new TemplateIsInUse());
      }
      return Promise.reject(err);
    }
  },

  getEventByID: async function (
    eventID: string,
  ): Promise<tEventResponse | null> {
    try {
      const config: tAxiosCustomConfig = {
        isLegacy: true,
        headers: undefined,
      };

      const response = await AxiosService.getById<tEventResponse>(
        'calendar/events',
        eventID,
        config,
      );

      return response;
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.API,
          method: 'getEventByID',
          operation: LOG_OPERATIONS.API_CALL,
        },
        'Error getting event by ID',
        error instanceof Error ? error : new Error(String(error)),
      );
      throw error;
    }
  },
};
